// src/components/CopyGuestLinkButton.jsx — CHANTIER LIEN COURT (côté admin).
//
// Bouton « copier le lien » réutilisable : demande au serveur le lien COURT
// `sejour.fullbnbzen.com/l/<code>` du couple (kind, target_token) puis le pose
// dans le presse-papier.
//
// 🔴 ÉCRITURE DANS LE GESTE — `copyGuestLinkNow()` est appelée en PREMIÈRE
// instruction du handler, sans aucun `await` avant elle : l'écriture presse-papier
// part dans la fenêtre d'activation utilisateur (Safari/WebKit et Firefox
// rejettent `writeText` — et `execCommand('copy')` — hors geste). Le lien LONG
// est posé immédiatement, le lien COURT le remplace en silence s'il arrive et
// si le navigateur l'accepte. Ne JAMAIS remettre un `await` avant cet appel.
//
// 🔴 REPLI — le lien LONG est toujours constructible côté client : sur échec
// d'émission (base indisponible, gate d'émission OFF, réseau coupé, refus
// serveur) c'est lui qui reste dans le presse-papier. JAMAIS un presse-papier
// vide, JAMAIS un lien cassé.
//
// L'URL affichée sous le bouton est celle RÉELLEMENT présente dans le
// presse-papier — jamais celle qu'on aurait voulu y mettre.
//
// UX : retour visuel « Lien copié » pendant 4 s, puis EFFACEMENT COMPLET du
// bloc (on ne bascule jamais un lien réellement copié vers « Copie manuelle » :
// l'admin recopierait à la main un lien déjà bon). « Copie manuelle : <url> »
// est réservé au cas où le navigateur a refusé la copie — et reste affiché.
//
// ⚠️ PAIEMENT EXCLU : ce bouton ne sait pas raccourcir un lien de paiement
// (kind refusé serveur). Le lien de paiement garde son bouton dédié dans
// l'onglet Paiements, en lien LONG — ne pas le brancher ici.

import { useEffect, useRef, useState } from "react"
import { Link2, Check } from "lucide-react"
import { DS } from "../lib/ui"
import { copyGuestLinkNow } from "../lib/guest-links-api"

export default function CopyGuestLinkButton({
  kind,
  token,
  label = "Copier le lien",
  testid = "copy-guest-link",
  compact = false,
}) {
  const [busy, setBusy] = useState(false)
  const [state, setState] = useState(null) // { url, copied, short }
  // Sérialise les copies : une réponse tardive ne doit jamais écraser
  // l'affichage d'une cible plus récente.
  const reqRef = useRef(0)

  // Un changement de cible remet l'affichage à zéro (jamais afficher l'URL
  // d'une autre réservation après re-render).
  useEffect(() => {
    reqRef.current += 1
    setState(null)
    setBusy(false) // la réponse en vol sera ignorée : ne pas laisser le bouton figé
  }, [kind, token])

  // Après 4 s on EFFACE tout (state → null). Surtout pas `copied:false` : le
  // bloc resterait rendu et étiquetterait « Copie manuelle » un lien pourtant
  // bien copié — l'admin le recopierait à la main ou douterait de son envoi.
  useEffect(() => {
    if (!state || !state.copied) return undefined
    const id = setTimeout(() => setState(null), 4000)
    return () => clearTimeout(id)
  }, [state])

  // ⚠️ NON-ASYNC ET SANS AWAIT AVANT `copyGuestLinkNow` : l'écriture
  // presse-papier doit partir dans la fenêtre d'activation utilisateur.
  function handleClick() {
    if (busy || !token) return
    const { settled } = copyGuestLinkNow(kind, token)
    const myReq = ++reqRef.current
    setBusy(true)
    settled.then(({ url, short, copied }) => {
      if (reqRef.current !== myReq) return
      setBusy(false)
      setState(url ? { url, short, copied } : null)
    })
  }

  const disabled = busy || !token

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        data-testid={testid}
        title={`Copier le lien à envoyer au voyageur${token ? "" : " (aucun lien disponible)"}`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          background: "#eef2f7",
          color: DS.navy,
          border: "none",
          borderRadius: 7,
          padding: compact ? "5px 10px" : "6px 12px",
          fontSize: 12,
          fontWeight: 600,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.55 : 1,
          fontFamily: DS.fontBody,
        }}
      >
        {state && state.copied ? <Check size={13} strokeWidth={2.4} /> : <Link2 size={13} strokeWidth={2.2} />}
        {busy ? "Génération…" : label}
      </button>
      {state && (
        <span
          data-testid={`${testid}-feedback`}
          style={{
            fontSize: 10,
            color: state.copied ? DS.green : DS.textMuted,
            wordBreak: "break-all",
            maxWidth: 340,
            textAlign: "right",
          }}
        >
          {state.copied ? "Lien copié : " : "Copie manuelle : "}
          {state.url}
        </span>
      )}
    </div>
  )
}
